import { PeriodType } from '@/lib/api/generated/model';
import type { PeriodBounds } from '@/lib/api/generated/model';

/**
 * Naming a limit's own renewal cadence (spec 091), distinct from `PeriodBounds` as used
 * by `PeriodStepper` — that type is the *usage-history* window being browsed. Here it is
 * only ever read for its `period_type`/`ends_at`, never for `key` or `is_current`.
 *
 * `WorkspaceLimitRow.period_type` (the admin table) carries no bounds of its own — only
 * `AppliedLimit.period` (the workspace's own usage summary) does. So the next renewal
 * instant for a *recurring* period is derived here from the type alone, mirroring the
 * boundary rules the service's own `usage/period.py` encodes (spec 083): daily resets at
 * the next UTC midnight, weekly at the next UTC Monday 00:00, monthly at the first of the
 * next UTC month. `period_type_changed_at` (FR-011/FR-012) only ever moves a period's
 * *start* earlier or later — it never moves the *end* a recurring boundary rule would
 * already produce — so no server-sent instant is needed to get the next renewal right.
 */

/** What every period type is called, once — shared so no surface invents its own word. */
export const PERIOD_LABEL: Record<PeriodType, string> = {
  [PeriodType.daily]: 'Daily',
  [PeriodType.weekly]: 'Weekly',
  [PeriodType.monthly]: 'Monthly',
  [PeriodType.none]: 'All time',
};

/** Absent from the response entirely (older data predating spec 091) reads as Monthly. */
export function periodLabel(periodType: PeriodType | null | undefined): string {
  return PERIOD_LABEL[periodType ?? PeriodType.monthly];
}

/** The literal copy for a lifetime cap — never a date (spec's US-02). */
export const LIFETIME_CAP_COPY = 'Never — lifetime cap';

/**
 * The next instant a recurring period type renews, from `now`.
 *
 * Matches `usage/period.py`'s boundary math exactly: daily and weekly always land on a
 * UTC calendar boundary, monthly on the first of the next UTC month.
 */
function nextBoundaryUtc(periodType: PeriodType, now: Date): Date {
  if (periodType === PeriodType.daily) {
    const next = new Date(now);
    next.setUTCHours(0, 0, 0, 0);
    next.setUTCDate(next.getUTCDate() + 1);
    return next;
  }
  if (periodType === PeriodType.weekly) {
    const next = new Date(now);
    next.setUTCHours(0, 0, 0, 0);
    // Monday-anchored weeks, matching `_weekly_period`: getUTCDay() 0=Sun..6=Sat, so the
    // days remaining until next Monday is 8 minus the ISO weekday, wrapped to 1-7.
    const isoWeekday = next.getUTCDay() === 0 ? 7 : next.getUTCDay();
    const daysToNextMonday = 8 - isoWeekday;
    next.setUTCDate(next.getUTCDate() + daysToNextMonday);
    return next;
  }
  // Monthly. Always the first of the *next* month, matching Daily/Weekly's rule that the
  // next renewal is a full cycle ahead even when `now` sits exactly on a boundary.
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
}

/**
 * Renewal copy for a period type: the lifetime-cap sentence, or a date converted to the
 * viewer's local timezone (spec 091 Clarifications).
 *
 * `now` is injectable for tests; defaults to the real clock (constitution §2.8 pattern).
 */
export function formatRenewal(
  periodType: PeriodType | null | undefined,
  now: Date = new Date(),
): string {
  const resolved = periodType ?? PeriodType.monthly;
  if (resolved === PeriodType.none) return LIFETIME_CAP_COPY;

  const renewsAt = nextBoundaryUtc(resolved, now);
  return renewsAt.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/**
 * The same renewal copy, but from an `AppliedLimit`'s own `PeriodBounds` when one is
 * available (the workspace's own usage summary carries `ends_at` directly rather than
 * needing it re-derived). Falls back to `formatRenewal` when there is no exclusive end
 * (the lifetime period, where `ends_at` is `null`).
 */
export function formatRenewalFromBounds(bounds: PeriodBounds): string {
  if (bounds.period_type === PeriodType.none || bounds.ends_at === null) {
    return LIFETIME_CAP_COPY;
  }
  return new Date(bounds.ends_at).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/**
 * A monthly period key as people say it: `2026-09` → "September 2026".
 *
 * Shared by the period stepper and the AI models page's "No runs in …" (spec 215), so
 * a month is never worded two ways.
 */
export function monthLabel(key: string): string {
  const [year, month] = key.split('-');
  return `${MONTHS[Number(month) - 1]} ${year}`;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
